import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  IconButton,
  Divider,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { SITES, HEIGHTS, MAX_FORECAST_TIME } from '../config/siteConfig';

function ControlPanel({ setHeight, setTime, resetView, setLoading, setSiteIndex, onPanelVisibilityChange }) {
  
  const [currentSiteIndex, setCurrentSiteIndex] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(HEIGHTS[0]);
  const [currentTime, setCurrentTime] = useState(0);
  const [initialSiteIndex, setInitialSiteIndex] = useState(0);
  const [initialHeight, setInitialHeight] = useState(HEIGHTS[0]);
  const [initialTime, setInitialTime] = useState(0);
  const [now, setNow] = useState(new Date());
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const maxForecastTime = MAX_FORECAST_TIME;

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (onPanelVisibilityChange) {
      onPanelVisibilityChange(isPanelVisible);
    }
  }, [isPanelVisible, onPanelVisibilityChange]);

  const adjustForecast = (adjustment) => {
    const newTime = currentTime + adjustment;
    if (newTime <= maxForecastTime && newTime >= 0) {
      setCurrentTime(newTime);
    }
  };

  const formatTime = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setMinutes(0, 0, 0); // Set minutes, seconds, and milliseconds to 0

    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    const hours = String(adjustedDate.getHours()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:00`;
  };

  const forecastTime = new Date(now.getTime() + currentTime * 3600000);
  const isChanged = currentHeight !== initialHeight || 
                   currentTime !== initialTime || 
                   currentSiteIndex !== initialSiteIndex;

  const handleSiteChange = (event) => {
    setCurrentSiteIndex(event.target.value);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: isPanelVisible ? 0 : '-250px',
        transition: 'left 0.3s',
        zIndex: 2,
        width: '250px',
        height: '100vh',
      }}
    >
      <IconButton
        onClick={() => setIsPanelVisible(!isPanelVisible)}
        sx={{
          position: 'absolute',
          top: 16,
          left: '250px',
          transition: 'left 0.3s',
          zIndex: 3,
          color: 'white',
        }}
      >
        {isPanelVisible ? <ArrowBackIos /> : <ArrowForwardIos />}
      </IconButton>

      <Paper elevation={3} sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <Stack spacing={3} className="control-panel" alignItems="center" justifyContent="start" sx={{ height: '100%' }}>
          <Typography variant="h6">Wind Platform</Typography>

          <Divider />

          <Stack direction="column" spacing={2} justifyContent="start" sx={{ flexGrow: 1, width: '100%' }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Height</InputLabel>
              <Select
                value={currentHeight}
                onChange={(e) => setCurrentHeight(e.target.value)}
                label="Height"
              >
                {HEIGHTS.map((height) => (
                  <MenuItem key={height} value={height}>
                    {height}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" fullWidth>
              <InputLabel id="site-select-label">Site</InputLabel>
              <Select
                labelId="site-select-label"
                value={currentSiteIndex}
                onChange={handleSiteChange}
                label="Site"
              >
                {SITES.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="column" alignItems="center" spacing={4}>
              <Typography>Now: {formatTime(now)}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button variant="outlined" onClick={() => adjustForecast(-1)}>{'<'}</Button>
                <Typography>+{currentTime} hour</Typography>
                <Button variant="outlined" onClick={() => adjustForecast(1)}>{'>'}</Button>
              </Stack>
              <Typography>Forecast: {formatTime(forecastTime)}</Typography>
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  console.log('Resetting view');
                  if (resetView) {
                    resetView();
                  }
                }}
                fullWidth
              >
                Reset View
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isChanged}
                onClick={() => {
                  setTimeout(() => {
                    if (currentSiteIndex !== initialSiteIndex) {
                      setLoading(true);
                    }
                    
                    setTimeout(() => {
                      setHeight(currentHeight);
                      setTime(currentTime);
                      setSiteIndex(currentSiteIndex);
                      setInitialHeight(currentHeight);
                      setInitialTime(currentTime);
                      setInitialSiteIndex(currentSiteIndex);
                    }, 50);
                  }, 10);
                }}
                fullWidth
              >
                Confirm
              </Button>
            </Stack>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold">App Controls</Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">Mobile/Tablet:</Typography>
                <Box>
                  <Typography variant="body2">• <b>Pinch/Spread:</b> Zoom in/out</Typography>
                  <Typography variant="body2">• <b>Tap + Drag:</b> Move view</Typography>
                  <Typography variant="body2">• <b>Long Press:</b> Open info panel</Typography>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">PC/Mac</Typography>
                <Box>
                  <Typography variant="body2">• <b>Mouse Wheel:</b> Zoom in/out</Typography>
                  <Typography variant="body2">• <b>Left Click + Drag:</b> Move view</Typography>
                  <Typography variant="body2">• <b>Right Click:</b> Open info panel</Typography>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  More info at <Link href="http://www.windhk.com" target="_blank" rel="noopener">www.windhk.com</Link>
                </Typography>
              </Box>
            </Box>

          </Stack>
        </Stack>
      </Paper>
    </div>
  );
}

export default ControlPanel;